import type { Visualizer } from './types';
import { clearBg, drawHud } from './types';

/** Scrolling mel spectrogram waterfall (stateful strip buffer). */
const COLS = 160;
const BINS = 64;
const strips: Float32Array[] = [];
let lastT = -1;

function pushColumn(freq: Uint8Array, energy: number): void {
  const col = new Float32Array(BINS);
  for (let y = 0; y < BINS; y++) {
    // log-ish bin mapping
    const t = y / (BINS - 1);
    const bin = Math.min(freq.length - 1, Math.floor(Math.pow(t, 1.55) * (freq.length - 1)));
    col[y] = (freq[bin] / 255) * (0.75 + energy * 0.45);
  }
  strips.push(col);
  while (strips.length > COLS) strips.shift();
}

export const spectrogramViz: Visualizer = {
  id: 'spectrogram',
  label: 'Spectrogram',
  draw(frame) {
    const { ctx, w, h, freq, bands, t } = frame;
    // new column ~ every frame; offline also advances t
    if (t !== lastT) {
      pushColumn(freq, bands.energy);
      lastT = t;
    }

    clearBg(ctx, w, h, 1);
    ctx.fillStyle = '#04080c';
    ctx.fillRect(0, 0, w, h);

    const padT = h * 0.12;
    const padB = h * 0.14;
    const plotH = h - padT - padB;
    const plotW = w * 0.92;
    const x0 = (w - plotW) / 2;
    const cellW = plotW / COLS;
    const cellH = plotH / BINS;

    for (let x = 0; x < strips.length; x++) {
      const col = strips[x];
      const px = x0 + x * cellW;
      for (let y = 0; y < BINS; y++) {
        const v = col[y];
        if (v < 0.04) continue;
        // bottom = bass
        const py = padT + plotH - (y + 1) * cellH;
        const hue = 160 + y * 1.8 + v * 40 + bands.highs * 20;
        const light = 28 + v * 48;
        ctx.fillStyle = `hsla(${hue}, 92%, ${light}%, ${0.35 + v * 0.65})`;
        ctx.fillRect(px, py, cellW + 0.5, cellH + 0.5);
      }
    }

    // live edge glow
    if (strips.length) {
      const edgeX = x0 + (strips.length - 1) * cellW;
      ctx.fillStyle = `rgba(86,240,200,${0.08 + bands.beat * 0.15})`;
      ctx.fillRect(edgeX, padT, 3, plotH);
    }

    // axis labels feel
    ctx.fillStyle = 'rgba(140,170,180,0.45)';
    ctx.font = `500 ${Math.max(10, Math.round(w * 0.012))}px ui-sans-serif, system-ui, sans-serif`;
    ctx.textBaseline = 'top';
    ctx.fillText('freq ↑', x0, padT - Math.max(14, h * 0.03));
    ctx.fillText('time →', x0 + plotW - 48, h - padB + 6);

    // waveform ribbon under plot
    const wy = h - padB * 0.45;
    ctx.beginPath();
    const step = Math.max(1, Math.floor(frame.wave.length / 200));
    for (let i = 0; i < frame.wave.length; i += step) {
      const x = x0 + (i / frame.wave.length) * plotW;
      const y = wy + (frame.wave[i] || 0) * padB * 0.35 * 10;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = `rgba(86,240,200,${0.35 + bands.energy * 0.4})`;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    drawHud(frame);
  },
};
